import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardDataProvider } from "@/context/DashboardDataContext";
import { PricingToggle } from "@/components/Pricing/PricingToggle";
import { PricingCards } from "@/components/Pricing/PricingCards";
import { PricingFAQ } from "@/components/Pricing/PricingFAQ";
import { PricingTrustedBy } from "@/components/Pricing/PricingTrustedBy";

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === "SIGNED_OUT" || !session) {
          navigate("/auth/signin");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session) {
        navigate("/auth/signin");
      }
      
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!session || !user) {
    return null;
  }

  return (
    <DashboardDataProvider>
      <DashboardShell>
        <main className="flex-1 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl py-8">
            <div className="w-full max-w-6xl mx-auto py-10 px-4">
              {/* HEADER */}
              <div className="flex flex-col items-center text-center mb-20">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-4">
                  Save hours, learn smarter.
                </h1>
                <p className="text-muted-foreground text-base md:text-lg lg:text-xl font-normal max-w-3xl">
                  Enjoy endless content uploads, chats, voice mode, recorded lectures, and more.
                </p>

                {/* BILLING TOGGLE */}
                <div className="mt-6">
                  <PricingToggle billingCycle={billingCycle} onChange={setBillingCycle} />
                </div>
              </div>

              {/* PRICING CARDS */}
              <PricingCards billingCycle={billingCycle} />

              {/* TRUSTED BY */}
              <div className="mt-20 hidden lg:block">
                <PricingTrustedBy />
              </div>

              {/* FAQ */}
              <div className="mt-20">
                <PricingFAQ />
              </div>
            </div>
          </div>
        </main>
      </DashboardShell>
    </DashboardDataProvider>
  );
}
